<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useDatapoints } from '@/composables/useDatapoints'
import SidebarNavFlyout from './SidebarNavFlyout.vue'
import type { NavItem } from '@/types/navigation.types'

interface Props {
	item: NavItem
	isAdmin: boolean
	rail: boolean
	flyoutOpen?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	isAdmin: false,
	rail: false,
	flyoutOpen: false,
})

// Emitted only when a real navigation happens (a leaf item or a child) — NOT when a
// group header is clicked to expand/collapse. Lets the sidebar close the mobile drawer
// on navigation without closing it every time a submenu is toggled.
const emit = defineEmits<{
	(e: 'navigate'): void
	(e: 'update:flyoutOpen', value: boolean): void
}>()

const route = useRoute()
const {
	enableLeadEnrichment,
	enableLookAlike,
	enablePreferredTitles,
	enableExternalSofoApi,
	disableCrmExport,
	enableBackOfficeIndex,
} = useDatapoints()

const featureFlagMap: Record<string, () => boolean> = {
	ENABLE_LEAD_ENRICHMENT: () => enableLeadEnrichment.value,
	ENABLE_LOOK_A_LIKE_SERVICE: () => enableLookAlike.value,
	ENABLE_PREFERRED_TITLES: () => enablePreferredTitles.value,
	ENABLE_EXTERNAL_SOFO_API: () => enableExternalSofoApi.value,
	DISABLE_CRM_EXPORT: () => !disableCrmExport.value,
	BACK_OFFICE_INDEX: () => enableBackOfficeIndex.value,
}

const visibleChildren = computed(
	() =>
		props.item.children?.filter((child) => {
			if (child.adminOnly && !props.isAdmin) return false
			if (child.featureFlag) {
				const check = featureFlagMap[child.featureFlag]
				if (check && !check()) return false
			}
			return true
		}) ?? [],
)

// The single source of truth for highlighting. Every link binds Vuetify's `active` and
// `aria-current` to this too, because vue-router alone gets the Dashboard wrong: '/' is the
// empty child of the root layout, and a link to an empty child counts as active on all of
// its siblings, i.e. every page.
function isActive(item: NavItem): boolean {
	if (item.to && (route.path === item.to || route.path.startsWith(item.to + '/'))) {
		return true
	}
	// Extra prefixes that should also light the item — e.g. a detail page whose path differs
	// from the list route it belongs to (/bulk-download/:id vs /bulk-downloads).
	if (item.activeMatch?.some((m) => route.path === m || route.path.startsWith(m + '/'))) {
		return true
	}
	if (item.href) {
		return window.location.pathname.toLowerCase().startsWith(item.href.toLowerCase())
	}
	return false
}

function isGroupActive(item: NavItem): boolean {
	if (isActive(item)) return true
	return item.children?.some((child) => isActive(child)) ?? false
}

const activeChild = computed(() => visibleChildren.value.find((child) => isActive(child))?.title ?? null)
</script>

<template>
	<!-- Collapsed rail: there is no room for an accordion, so the group's children open
	     in a flyout beside the rail (hover, click, Enter or ArrowRight) -->
	<SidebarNavFlyout
		v-if="rail && visibleChildren.length"
		:model-value="flyoutOpen"
		:title="item.title"
		:children="visibleChildren"
		:active-child="activeChild"
		@update:model-value="emit('update:flyoutOpen', $event)"
		@navigate="emit('navigate')"
	>
		<template #activator="{ props: flyoutProps }">
			<v-list-item
				v-bind="flyoutProps"
				:class="{ 'nav-item--active': isGroupActive(item), 'nav-item--open': flyoutOpen }"
				class="nav-item nav-item--flyout"
			>
				<template #prepend>
					<i
						:class="item.icon"
						class="nav-item__icon"
					/>
				</template>
				<v-list-item-title class="nav-item__title">{{ item.title }}</v-list-item-title>
				<span
					class="nav-item__flyout-notch"
					aria-hidden="true"
				/>
			</v-list-item>
		</template>
	</SidebarNavFlyout>

	<!-- Expandable group -->
	<v-list-group
		v-else-if="item.children && visibleChildren.length"
		:value="item.title"
	>
		<template #activator="{ props: groupProps }">
			<v-list-item
				v-bind="groupProps"
				:class="{ 'nav-item--active': isGroupActive(item) }"
				class="nav-item"
			>
				<template #prepend>
					<i
						:class="item.icon"
						class="nav-item__icon"
					/>
				</template>
				<v-list-item-title class="nav-item__title">{{ item.title }}</v-list-item-title>
			</v-list-item>
		</template>

		<v-list-item
			v-for="child in visibleChildren"
			:key="child.title"
			:to="child.to"
			:href="child.href"
			:target="child.external ? '_blank' : undefined"
			:rel="child.external ? 'noopener' : undefined"
			:active="isActive(child)"
			:aria-current="isActive(child) ? 'page' : undefined"
			:class="{ 'nav-item--active': isActive(child) }"
			class="nav-item nav-item--child"
			density="compact"
			@click="emit('navigate')"
		>
			<template #prepend>
				<i
					:class="child.icon"
					class="nav-item__icon nav-item__icon--small"
				/>
			</template>
			<v-list-item-title class="nav-item__title">{{ child.title }}</v-list-item-title>
		</v-list-item>
	</v-list-group>

	<!-- Single item -->
	<v-tooltip
		v-else
		:disabled="!rail"
		:text="item.title"
		location="end"
		:open-delay="300"
	>
		<template #activator="{ props: tooltipProps }">
			<v-list-item
				v-bind="tooltipProps"
				:to="item.to"
				:href="item.href"
				:target="item.external ? '_blank' : undefined"
				:rel="item.external ? 'noopener' : undefined"
				:active="isActive(item)"
				:aria-current="isActive(item) ? 'page' : undefined"
				:class="{ 'nav-item--active': isActive(item) }"
				class="nav-item"
				@click="emit('navigate')"
			>
				<template #prepend>
					<i
						:class="item.icon"
						class="nav-item__icon"
					/>
				</template>
				<v-list-item-title class="nav-item__title">{{ item.title }}</v-list-item-title>
				<template
					v-if="item.external"
					#append
				>
					<i class="fa-solid fa-arrow-up-right-from-square nav-item__external" />
				</template>
			</v-list-item>
		</template>
	</v-tooltip>
</template>

<style scoped lang="scss">
.nav-item {
	position: relative;
	margin: 2px 8px;
	border-radius: 8px;
	overflow: hidden;
	transition: background-color 200ms ease;

	// Active accent bar: scales in when the item becomes active
	// (motion collapses to an instant state change under reduced motion)
	&::before {
		content: '';
		position: absolute;
		left: 0;
		top: 20%;
		bottom: 20%;
		width: 3px;
		border-radius: 2px;
		background: $tami-cyan;
		transform: scaleY(0);
		transition: transform 250ms $ease-out-expo;
	}

	&:hover {
		background-color: rgba(255, 255, 255, 0.08);
	}

	&--active {
		background-color: rgba(255, 255, 255, 0.12);

		&::before {
			transform: scaleY(1);
		}
	}

	&--child {
		margin-left: 16px;
	}

	// Rail group: stays lit while its flyout is open, so the panel reads as coming from it
	&--open {
		background-color: rgba(255, 255, 255, 0.08);
	}

	// Rail group: toolbar-style corner notch hinting that more pages open from here.
	// A real element, because VListItem already owns ::after for its focus outline.
	&__flyout-notch {
		position: absolute;
		right: 3px;
		bottom: 3px;
		width: 0;
		height: 0;
		border-style: solid;
		border-width: 0 0 5px 5px;
		border-color: transparent transparent currentColor;
		color: rgba(255, 255, 255, 0.3);
		transition:
			transform 250ms $ease-out-expo,
			color 200ms ease;
	}

	&--flyout:hover &__flyout-notch,
	&--open &__flyout-notch {
		color: $tami-cyan;
		transform: translate(1px, 1px);
	}

	&__icon {
		width: 20px;
		font-size: 16px;
		text-align: center;
		color: rgba(255, 255, 255, 0.7);
		margin-right: 12px;
		transition:
			transform 150ms $ease-standard,
			color 200ms ease;

		&--small {
			font-size: 13px;
		}
	}

	&:hover &__icon {
		transform: translateX(2px);
	}

	&__title {
		font-size: 0.875rem;
		font-weight: 400;
		color: rgba(255, 255, 255, 0.9);
	}

	&__external {
		font-size: 10px;
		opacity: 0.5;
	}
}

.nav-item--active .nav-item__icon {
	color: $tami-cyan;
}

.nav-item--active .nav-item__title {
	color: #ffffff;
	font-weight: 500;
}

// Shrink the v-list-group expand/collapse chevron
:deep(.v-list-group__header .v-list-item__append .v-icon) {
	font-size: 14px !important;
	min-width: 20px;
}

// Fix text overflow in nav items
:deep(.v-list-item-title) {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
</style>
